<template>
  <v-card>
    <v-file-input
      ref="refFileInput"
      dense
      outlined
      multiple
      show-size
      hide-details
      validate-on-blur
      :label="label"
      :prepend-icon="null"
      :prepend-inner-icon="icons.mdiPaperclip"
      :rules="rules"
      @change="handleUploadArquivos"
    />

    <v-data-table
      hide-default-header
      hide-default-footer
      class="elevation-4"
      :headers="headers"
      :items="listaDeArquivosLocale"
      :no-data-text="noDataMessage"
    >
      <template v-slot:item.nome="{ item }">
        <v-chip
          color="primary"
          outlined
          @click="downloadArquivo(item)"
        >
          {{ item.nome }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-end">
          <v-icon
            :disabled="loading"
            color="error"
            @click="removerArquivo(item)"
          >
            {{ icons.mdiDelete }}
          </v-icon>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import Vue from 'vue'
import { computed, ref, watchEffect } from '@vue/composition-api'
import { mdiPaperclip, mdiDelete } from '@mdi/js'

export default {
  emits: ['update:lista-de-arquivos'],

  props: {
    label: {
      type: String,
      default: 'Adicionar arquivo',
    },
    noDataMessage: {
      type: String,
      default: 'Nenhum arquivo adicionado',
    },
    listaDeArquivos: {
      type: Array,
      default: () => [],
    },
    qtdMaximaArquivos: {
      type: Number,
      default: null,
    },
    tamanhoMaximoArquivoMb: {
      type: Number,
      default: null,
    },
    loading: Boolean,
  },

  setup(props, { emit }) {
    const ONE_MB = 1_048_576

    const listaDeArquivosLocale = ref([])
    watchEffect(() => { listaDeArquivosLocale.value = JSON.parse(JSON.stringify(props.listaDeArquivos)) })

    function updatePropListaDeArquivos() {
      emit('update:lista-de-arquivos', listaDeArquivosLocale.value.map(it => ({ nome: it.nome, dataUrl: it.dataUrl })))
    }

    function findFilesNaListaDeArquivos(files) {
      let index = 0
      let found = false

      while (index < files.length && !found) {
        const file = files[index]

        found = !!listaDeArquivosLocale.value.find(existingFile => existingFile.nome === file.name)
        index += 1
      }
      return found
    }

    function readFileAsDataUrl(file) {
      return new Promise(resolve => {
        const reader = new FileReader()
        reader.onload = () => { resolve({ nome: file.name, dataUrl: reader.result, lastModified: file.lastModified }) }
        reader.readAsDataURL(file)
      })
    }

    async function addFilesToArray(files) {
      const readers = []

      files.forEach(file => { readers.push(readFileAsDataUrl(file)) })

      return Promise.all(readers).then(values => {
        values.forEach(it => {
          listaDeArquivosLocale.value.push({
            nome: it.nome,
            dataUrl: it.dataUrl,
            lastModified: it.lastModified,
          })
        })
      })
    }

    async function handleUploadArquivos(files) {
      if (!files) return

      const jaPossuiArquivoComOMesmoNome = findFilesNaListaDeArquivos(files)
      const quantidadeMaximaExcedida = props.qtdMaximaArquivos && listaDeArquivosLocale.value.length + files.length > props.qtdMaximaArquivos
      const tamanhoMaximoExcedido = props.tamanhoMaximoArquivoMb && files.some(file => file.size / ONE_MB > props.tamanhoMaximoArquivoMb)

      if (jaPossuiArquivoComOMesmoNome) {
        Vue.$toast.warning('Não são permitidos anexos com os mesmos nomes.')
      }
      if (quantidadeMaximaExcedida) {
        Vue.$toast.warning('Máximo de 6 anexos')
      }
      if (tamanhoMaximoExcedido) {
        Vue.$toast.warning(`O anexo não pode ter mais de ${props.tamanhoMaximoArquivoMb}MB.`, { timeout: false })
      }
      if (jaPossuiArquivoComOMesmoNome || quantidadeMaximaExcedida || tamanhoMaximoExcedido) {
        return
      }

      await addFilesToArray(files)
      updatePropListaDeArquivos()
    }

    function downloadArquivo(item) {
      if (item.url) {
        window.open(item.url)
      } else if (item.dataUrl) {
        const parts = item.dataUrl.split(';base64,')
        const imageType = parts[0].split(':')[1]
        const decodedData = window.atob(parts[1])
        const uInt8Array = new Uint8Array(decodedData.length)

        for (let i = 0; i < decodedData.length; i += 1) {
          uInt8Array[i] = decodedData.charCodeAt(i)
        }
        let link = new Blob([uInt8Array], { type: imageType })
        link = URL.createObjectURL(link)
        window.open(`${link}`)
      }
    }

    function removerArquivo(item) {
      const editedIndex = listaDeArquivosLocale.value.indexOf(item)
      listaDeArquivosLocale.value.splice(editedIndex, 1)
      updatePropListaDeArquivos()
    }

    const refFileInput = ref(null)

    const rules = computed(() => ([
      files => {
        if (!files) return true
        if (!props.tamanhoMaximoArquivoMb) return true

        const tamanhoMaximoExcedido = files.every(f => f.size / ONE_MB <= props.tamanhoMaximoArquivoMb)
        if (tamanhoMaximoExcedido) { refFileInput.value.reset() }

        return tamanhoMaximoExcedido
      },
    ]))

    const headers = [
      { text: 'Nome', value: 'nome' },
      { text: 'Actions', value: 'actions' },
    ]

    return {
      listaDeArquivosLocale,
      handleUploadArquivos,
      downloadArquivo,
      removerArquivo,
      rules,
      headers,
      refFileInput,
      icons: {
        mdiPaperclip,
        mdiDelete,
      },
    }
  },
}
</script>
